/**
 * « Mes primes » — the premiums list (fiches 22749 / 22988), registered as
 * `extranet.premiumsListCard`.
 *
 * - Integrated `DataList` over inline rows (`WS2_EA_TAB_QUI`, loaded by the
 *   page): columns `assure`, `produit`, `numeroContrat`, `reference`,
 *   `periodeDu`, `periodeAu`, `ttc`, `situation` (badge), `actions`
 *   (« Documents » button) — see `./premiumsListConfig.tsx`.
 * - Toolbar (fiche 22988) in a card detached above the table: the search
 *   « Rechercher par réf, produit, assuré… » and the situation select
 *   (stable filter id `situation`: « Toutes situations » + the
 *   `WS2_EA_SEL_STATUT_PRIME` options, fetched here). Both are
 *   **server-side**: the toolbar state goes up through `onFiltersChange`
 *   (search debounced 300 ms → `filtre`, select → `situation`, blank ⇒ not
 *   sent) and the page re-fetches `WS2_EA_TAB_QUI`; nothing is filtered
 *   client-side.
 * - Hosted by the shared `DataTableCard` (detached toolbar, server-filtered
 *   inline mode), which merges `_layoutOverrides` (columns rename / hide /
 *   show / reorder / tooltips, default sort, filter + search arms, title) and
 *   publishes the base config plus the row action; the `actions.documents`
 *   bucket relabels / hides the per-row button (`setActionLabel` /
 *   `hideAction`), resolved here.
 * - No base title: the page header carries « Mes primes » and the « {n}
 *   primes » subtitle; a `setTitle` rule adds a card title.
 * - Below 768px `DataList` renders its own mobile card layout and the
 *   toolbar stacks.
 */

import { useCallback, useMemo } from 'react';
import { Receipt } from 'lucide-react';

import type { LayoutOverrides } from '@components/shared/configurable';

import {
  SEL_STATUT_PRIME_ENDPOINT,
  premiumsService,
  type Premium,
  type PremiumsListFilters,
} from '../../api/premiumsService';
import { useSelOptions } from '../../hooks/useSelOptions';
import { DataTableCard } from '../shared/DataTableCard';
import type { ListToolbarQuery } from '../shared/listToolbarConfig';
import {
  PREMIUM_DOCUMENTS_ACTION,
  PREMIUM_DOCUMENTS_ACTION_KEY,
  PREMIUM_SITUATION_FILTER_ID,
  PREMIUMS_LIST_ACTIONS,
  PREMIUMS_LIST_CARD_ID,
  buildPremiumsListConfig,
  type PremiumListRow,
  type ResolvedPremiumAction,
} from './premiumsListConfig';

export interface PremiumsListCardProps {
  /** Premiums in API order (`WS2_EA_TAB_QUI` view models, already filtered by VEOS). */
  premiums: readonly Premium[];
  loading?: boolean;
  error?: string | null;
  /** « Documents » click on a row — opens the documents panel (page-wired). */
  onOpenDocuments?: (premium: Premium) => void;
  /** Toolbar state as `WS2_EA_TAB_QUI` filters (on mount and on change) — page-wired. */
  onFiltersChange?: (filters: PremiumsListFilters) => void;
  /** Injected by `withComponentRules` — never passed by hand. */
  _layoutOverrides?: LayoutOverrides;
}

export function PremiumsListCard({
  premiums,
  loading = false,
  error = null,
  onOpenDocuments,
  onFiltersChange,
  _layoutOverrides,
}: Readonly<PremiumsListCardProps>) {
  const situationOptions = useSelOptions(
    SEL_STATUT_PRIME_ENDPOINT,
    premiumsService.fetchPremiumSituationOptions,
  );

  // The « Documents » button resolved against the tenant overrides (identity
  // at baseline).
  const actionOverride = _layoutOverrides?.actions?.[PREMIUM_DOCUMENTS_ACTION_KEY];
  const actionLabel = actionOverride?.label ?? PREMIUM_DOCUMENTS_ACTION.label;
  const actionVisible = actionOverride?.visible ?? true;
  const documentsAction = useMemo<ResolvedPremiumAction>(
    () => ({ label: actionLabel, visible: actionVisible }),
    [actionLabel, actionVisible],
  );

  // Per-row context rebuilt with the rows / action / callback so the
  // singleton cell renderer always reads fresh closures.
  const rows = useMemo<PremiumListRow[]>(
    () =>
      premiums.map((premium) => ({
        ...premium,
        __ctx: {
          documentsAction,
          onOpenDocuments: () => {
            onOpenDocuments?.(premium);
          },
        },
      })),
    [premiums, documentsAction, onOpenDocuments],
  );

  const baseConfig = useMemo(
    () => buildPremiumsListConfig(rows, situationOptions),
    [rows, situationOptions],
  );

  // Toolbar → VEOS filters: search → `filtre`, situation select → `situation`.
  const handleToolbarChange = useCallback(
    (query: ListToolbarQuery) => {
      onFiltersChange?.({
        filtre: query.search,
        situation: query.filters[PREMIUM_SITUATION_FILTER_ID] ?? '',
      });
    },
    [onFiltersChange],
  );

  return (
    <DataTableCard
      componentId={PREMIUMS_LIST_CARD_ID}
      icon={Receipt}
      config={baseConfig}
      rowCount={premiums.length}
      loading={loading}
      error={error}
      toolbar
      toolbarPlacement="detached"
      onToolbarChange={handleToolbarChange}
      publishedActions={PREMIUMS_LIST_ACTIONS}
      _layoutOverrides={_layoutOverrides}
    />
  );
}
